import { useMemo } from 'react';
import { API_CONFIG } from '../lib/apiConfig';

interface UseSoilCarbonOverlayResult {
  /** Leaflet-ready tile template with {z}/{x}/{y} placeholders, or null when disabled or unconfigured. */
  tileUrl: string | null;
  /**
   * Complete `Authorization` header value for Flux (HTTP Basic), or null
   * when no token is configured. Callers must use AuthTileLayer rather than
   * the stock TileLayer so this header reaches the tile request.
   */
  authHeader: string | null;
  /** True when a tile URL AND auth header both resolved. */
  isAvailable: boolean;
  /**
   * Top of the source tile pyramid (Flux 404s past it). Plumbed to
   * AuthTileLayer as `maxNativeZoom` so Leaflet upscales the z=10 tiles
   * past z=10 instead of fetching coords that don't exist.
   */
  maxNativeZoom: number;
  /**
   * Rescale used in the tile URL (Mg C/ha). Doubles as the legend domain so
   * the overlay legend stays in lockstep with what the server renders.
   */
  rescale: [number, number];
}

// btoa handles ASCII only; the username `ctrees` and Flux tokens are ASCII,
// so this is safe. Matches useAgbOverlay — kept local here (not exported)
// so each hook stays self-contained.
const basicAuthHeader = (username: string, token: string): string =>
  `Basic ${btoa(`${username}:${token}`)}`;

// Flux's WebMercatorQuad endpoint expects path segments ordered as
// {tileMatrix}/{tileRow}/{tileCol} — i.e. /{z}/{y}/{x} — not the standard
// XYZ /{z}/{x}/{y}. Leaflet substitutes placeholders by name, so ordering
// in the template is what matters. Unlike AGB, soil carbon requires an
// explicit `colorscalerange` because the raw values span depth-specific
// ranges that Flux's default rescale doesn't cover.
const buildTileUrl = (
  host: string,
  datasetPath: string,
  variable: string,
  style: string,
  colorscalerange: [number, number],
): string => {
  const params = new URLSearchParams({
    variables: variable,
    colorscalerange: `${colorscalerange[0]},${colorscalerange[1]}`,
    style,
    width: '256',
    height: '256',
    f: 'png',
    render_errors: 'false',
  });
  return `${host}${datasetPath}/{z}/{y}/{x}?${params.toString()}`;
};

/**
 * Resolves an Earthmover Flux tile URL for the Soil Organic Carbon 100 m
 * dataset at the given `depth` profile (30 cm topsoil or 100 cm full profile).
 *
 * Changing `depth` re-resolves a new URL pointing at the sibling dataset,
 * which invalidates Leaflet's tile cache and causes a fresh fetch. The two
 * profiles have different absolute value ranges, so the `colorscalerange`
 * also swaps — the returned `rescale` tracks it so the legend stays honest.
 */
export const useSoilCarbonOverlay = (
  depth: 30 | 100,
  enabled: boolean = true,
): UseSoilCarbonOverlayResult => {
  return useMemo(() => {
    const flux = API_CONFIG.earthmoverFlux;
    const cfg = flux.soilCarbon;
    const depthCfg = cfg.depths[depth];
    const baseResult = {
      maxNativeZoom: cfg.maxNativeZoom,
      rescale: depthCfg.legendRescale,
    };
    // No token, or overlay disabled → null out the URL so callers never
    // accidentally hit Flux. DetailMapView already hides the overlay when
    // isAvailable is false, so the UI degrades cleanly in environments
    // without a token wired (local dev, staging pre-propagation).
    const tokenMissing = !flux.token;
    if (!enabled || tokenMissing) {
      return {
        ...baseResult,
        tileUrl: null,
        authHeader: null,
        isAvailable: false,
      };
    }
    return {
      ...baseResult,
      tileUrl: buildTileUrl(
        flux.tileHost,
        depthCfg.datasetPath,
        cfg.variable,
        cfg.style,
        depthCfg.colorscalerange,
      ),
      authHeader: basicAuthHeader(flux.username, flux.token),
      isAvailable: true,
    };
    // flux.token, flux.tileHost, and every cfg.* field are read from
    // import.meta.env / module-level constants and are fixed at module load,
    // so they're intentionally omitted from the dep list.
  }, [depth, enabled]);
};
